// Client-side analytics BUFFERING — pure, no DOM, no fetch.
//
// The shell must never POST once per event: opening five apps in ten seconds
// would be five requests, and `session.end` fires while the page is being torn
// down. So events accumulate here and leave in batches. All the decisions about
// *when* a batch is ready live in this file so `node --test` can assert them
// without a browser.
//
// This file is `.mjs` (not `.ts`) on purpose: `node --test` cannot import a
// `.ts` source. Types are mirrored in ./analytics-buffer.d.mts.
//
// KEEP IN SYNC with ./analytics-buffer.d.mts.

/** Flush on a timer even when the user is idle, so a long visit isn't lost. */
export const FLUSH_INTERVAL_MS = 15_000;

/** Flush as soon as this many events are waiting. */
export const FLUSH_AT_COUNT = 20;

/**
 * Hard ceiling on buffered events. Reached only when the endpoint is down —
 * without it, a desktop left open against a dead server grows a list forever.
 */
export const MAX_BUFFERED = 200;

/** sessionStorage key holding the visit id. */
export const SESSION_KEY = "arclog.analytics.sid";

/**
 * A bounded FIFO of pending events.
 *
 * On overflow the OLDEST event is dropped, not the newest: the recent activity
 * is what a still-running session is trying to report, and the one event that
 * genuinely must not be lost — `login` — is captured server-side where no
 * client buffer can drop it.
 *
 * @param {{ maxBuffered?: number, flushAt?: number }} [opts]
 */
export function createEventBuffer(opts = {}) {
  const maxBuffered =
    Number.isFinite(opts.maxBuffered) && opts.maxBuffered > 0 ? opts.maxBuffered : MAX_BUFFERED;
  const flushAt = Number.isFinite(opts.flushAt) && opts.flushAt > 0 ? opts.flushAt : FLUSH_AT_COUNT;
  /** @type {any[]} */
  let pending = [];
  let dropped = 0;

  return {
    /** @param {any} event */
    push(event) {
      if (!event) return;
      pending.push(event);
      while (pending.length > maxBuffered) {
        pending.shift();
        dropped += 1;
      }
    },
    /** True once a batch is worth sending. */
    shouldFlush() {
      return pending.length >= flushAt;
    },
    size() {
      return pending.length;
    },
    /** Events lost to overflow since creation — useful when debugging gaps. */
    droppedCount() {
      return dropped;
    },
    /** Remove and return everything pending. Empty array when idle. */
    take() {
      if (!pending.length) return [];
      const out = pending;
      pending = [];
      return out;
    },
    /**
     * Put events back after a failed send, oldest-first, respecting the cap.
     * Without this a transient network blip silently eats a whole batch.
     * @param {any[]} events
     */
    restore(events) {
      if (!Array.isArray(events) || !events.length) return;
      pending = events.concat(pending);
      while (pending.length > maxBuffered) {
        pending.shift();
        dropped += 1;
      }
    },
  };
}

/**
 * Resolve the id for THIS visit.
 *
 * Persisted in `sessionStorage` (not `localStorage`) on purpose: a reload or an
 * in-tab navigation continues the same visit, while closing the tab ends it.
 * That is exactly the boundary "how many visits" wants to count. localStorage
 * would make one lifetime-long session per browser; a plain variable would make
 * every reload a new one.
 *
 * @param {{ getItem(k: string): string | null, setItem(k: string, v: string): void } | null} storage
 * @param {() => string} mint generates a fresh id
 * @returns {string}
 */
export function resolveSessionId(storage, mint) {
  if (!storage) return mint();
  let id = null;
  try {
    id = storage.getItem(SESSION_KEY);
  } catch {
    // Private-mode / disabled storage: a per-load id is still better than none.
    return mint();
  }
  if (typeof id === "string" && id.trim()) return id;
  const fresh = mint();
  try {
    storage.setItem(SESSION_KEY, fresh);
  } catch {
    /* not fatal — the id just won't survive a reload */
  }
  return fresh;
}
